import React, { useRef, useState } from 'react';
import { useClickOutside } from 'hooks/useClickOutside';

interface RowsPerPageSelectProps {
  value: number;
  onChange: (value: number) => void;
  options?: number[];
}

/**
 * Rows-per-page dropdown that always opens downward with a controlled popover,
 * instead of a native <select> whose option list the browser may flip upward
 * (and overlap the table) when the control sits near the bottom of the page.
 */
const RowsPerPageSelect: React.FC<RowsPerPageSelectProps> = ({
  value,
  onChange,
  options = [10, 50, 100],
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative w-fit" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-3 min-w-[72px] pl-3 pr-2 py-2 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value}</span>
        <i
          className={`st-arrow-right text-xs transition-transform ${
            open ? '-rotate-90' : 'rotate-90'
          }`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          className="absolute left-0 top-full mt-1 z-20 w-full min-w-[72px] bg-white rounded-lg shadow-lg border border-lightgray-100 overflow-hidden"
          role="listbox"
        >
          {options.map((opt) => (
            <li key={opt}>
              <button
                type="button"
                role="option"
                aria-selected={opt === value}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-lightblue-100 transition-colors ${
                  opt === value ? 'bg-lightblue-100 text-royalblue-100 font-medium' : 'text-grease-100'
                }`}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RowsPerPageSelect;
